import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, message } from '@/components';
import { WhiteboardInstance, DrawPlugin } from '@/types/whiteboard';
import { note, rtc } from '@/models';
import { useSize } from 'ahooks';
import s from './index.less';
import RTCModel from '@/models/rtc';
import MeetingModal from '@/models/meeting';
import { inject, observer } from 'mobx-react';
import {
  onCaptureSourceChang,
  onGenerateSnapshot,
  onSelectTool,
  snapshot,
  loadEnd,
  toolsStateChanged,
} from '@/utils/Bridge';
import { getWhiteBoardPlatform } from '@/utils/common';
import WhiteBoardTools, { ToolTye } from './utils';
import NoteTools from './NoteTools';
import OpenNoteBtn from './NoteTools/components/OpenNoteBtn';
import { NotePermission } from '@/models/note';
import { elkLog } from '@/utils/elkReport';
import { Platform } from '@/utils/platform';

interface NoteProps {
  channelId?: number;
  meetingId?: string;
  uid: number;
  uname: string;
  appkey: string;
  nonce: string;
  curTime: string;
  checksum: string;
  whiteboardChannelId: string;
  originWidth?: number;
  originHeight?: number;
  RTCInstance?: RTCModel;
  meeting?: MeetingModal;
}
const Note: React.FC<NoteProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
  };
})(
  observer((props) => {
    const {
      appkey,
      nonce,
      checksum,
      curTime,
      whiteboardChannelId,
      uid,
      uname,
      originWidth,
      originHeight,
    } = props;

    const { meeting } = props;

    const [remoteSizeData, setRemoteSizeData] = useState({
      width: 0,
      height: 0,
    });
    const tools = useRef<WhiteBoardTools>();
    const domRef = useRef<any>();
    const containerRef = useRef<HTMLDivElement>(null);
    const noteInstance = useRef<WhiteboardInstance>();
    const drawPlugin = useRef<DrawPlugin>();
    const [state, setState] = useState<'none' | 'initializing' | 'done'>('none');
    const noteConatinerSize = useSize(domRef) as any;
    const redoundoState = useRef<{ redo: boolean; undo: boolean }>({
      redo: false,
      undo: false,
    });

    const initNoteInstance = () => {
      console.log('--state', state);
      console.log(
        '--白板参数',
        `appkey:${appkey}`,
        `nonce:${nonce}`,
        `checksum:${checksum}`,
        `curTime:${curTime}`,
        `whiteboardChannelId:${whiteboardChannelId}`,
        `uid:${uid}`,
        `uname:${uname}`,
        `originWidth:${originWidth}`,
        `originHeight:${originHeight}`
      );
      if (
        !appkey ||
        !nonce ||
        !checksum ||
        !curTime ||
        !whiteboardChannelId ||
        !uid ||
        !uname ||
        !originWidth ||
        !originHeight
      ) {
        return;
      }
      if (state === 'none') {
        setState('initializing');
        console.log('--初始化白板');
        const platform = getWhiteBoardPlatform();
        noteInstance.current = WhiteBoardSDK.getInstance({
          appKey: appkey,
          uid: uid,
          nickname: uname,
          container: containerRef.current,
          platform,
          record: false, // 是否开启录制
          drawPluginParams: {
            disableAutoResize: false,
          },
          getAuthInfo: async () => {
            return {
              nonce,
              checksum,
              curTime,
            };
          },
        });
        noteInstance.current
          ?.joinRoom(
            {
              channel: whiteboardChannelId.toString(),
              createRoom: true, // 云信建议这里设置 true
            },
            {
              onconnected: () => {
                elkLog('批注连接成功');
                console.log('whiteboard connected, cid=%s, uid=%s', whiteboardChannelId, uid);
              },
              onwillreconnect: () => {
                elkLog('批注即将重连');
                console.log('whiteboard will reconnect');
              },
              ondisconnected: (err: any) => {
                elkLog('批注重连失败', err);
                console.log('whiteboard on disconnected', err);
              },
            }
          )
          .then((plugin) => {
            elkLog('批注joinRoom成功');
            drawPlugin.current = plugin;
            window.drawPlugin = plugin;
            plugin.enableDraw(true);
            plugin.setAppConfig({
              showCursorNickname: true,
              showLaserNickname: true,
              showSelectNickname: true,
              canvasBgColor: 'transparent',
              useSoftKeyboard: isPad || false,
              disableAutoResize: false,
            });
            plugin.enableCameraTransform(false);
            if (!isMobile && !isiOSApp && !isAndroidApp) {
              plugin.setFontFamily({
                fontFamily:
                  'Chinese Quote, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Helvetica Neue, Helvetica, Arial, sans-serif',
              });
            }
            loadEnd()
              .then(() => {
                elkLog('loadEnd调用成功');
                setState('done');
              })
              .catch((err) => {
                elkLog('loadEnd调用失败', err);
              }); // 调用客户端显示前端web页面
            meeting?.setWhiteboardLoading(false);
            tools.current = new WhiteBoardTools(plugin);
            plugin.on('event:appState:change', (stateName, value) => {
              if (stateName === 'redoCount') {
                const _redo = value === 0 ? false : true;
                if (redoundoState.current.redo !== _redo) {
                  redoundoState.current.redo = _redo;
                  toolsStateChanged({
                    toolsState: [
                      {
                        toolType: ToolTye.REDO,
                        enabled: _redo,
                      },
                    ],
                  });
                }
              }
              if (stateName === 'undoCount') {
                const _undo = value === 0 ? false : true;
                if (redoundoState.current.undo !== _undo) {
                  redoundoState.current.undo = _undo;
                  toolsStateChanged({
                    toolsState: [
                      {
                        toolType: ToolTye.UNDO,
                        enabled: _undo,
                      },
                    ],
                  });
                }
              }
            });
            //@ts-ignore
            // plugin.on('container:resize', () => {
            //   console.log('-----container:resize------');
            //   if (originWidth && originHeight && !oneceResizeRef.current) {
            //     console.log('---setCameraBound111------');
            //     changeCameraBound(originWidth, originHeight);
            //     oneceResizeRef.current = true;
            //   }
            // });
            if (originWidth && originHeight) {
              console.log('---setCameraBound111------');
              changeCameraBound(originWidth, originHeight);
            }
          })
          .catch((e) => {
            elkLog('批注joinRoom成失败', e);
            console.error('failed to enable whiteboard', e);
            message.error({ content: e.message || String(e) });
            meeting?.setWhiteboardLoading(false);
          });
      }
    };

    useEffect(() => {
      initNoteInstance();
    }, [
      appkey,
      nonce,
      checksum,
      curTime,
      whiteboardChannelId,
      uid,
      uname,
      state,
      originWidth,
      originHeight,
    ]);

    const screenDomSize = useMemo(() => {
      const { width, height } = rtc!.calcVideoSize(
        noteConatinerSize?.width || 10, //云信sdk设置画布原点和宽高的时候dom元素不能为0, 预先设置宽高,可以在初始化白板画布的时候 设置链接带过来的大小,  修复受限于无宽高设置无效的的问题
        noteConatinerSize?.height || 10,
        remoteSizeData.width,
        remoteSizeData.height
      );
      return {
        width: width,
        height: height,
      };
    }, [remoteSizeData, noteConatinerSize]);

    const changeCameraBound = (width: number, height: number) => {
      drawPlugin?.current?.setCameraBound({
        centerX: width / 2,
        centerY: height / 2,
        width: width,
        height: height,
      });
    };

    // 提供给客户端的批注链接，首次获取到共享源大小后设置批注世界坐标系
    useEffect(() => {
      if (originWidth && originHeight) {
        console.log('初始化changeCameraBound', originWidth, originHeight);
        setRemoteSizeData({
          width: originWidth,
          height: originHeight,
        });
      }
    }, [originHeight, originWidth]);

    useEffect(() => {
      // 走客户端监听 源大小变化 不可以在这这里设置画布, 必须在video-resize事件中,不然拖着源的时候 sdk的画面还没变 就看到批注在动了
      onCaptureSourceChang((data) => {
        setRemoteSizeData(data);
        console.log('收到通知changeCameraBound', data);
        changeCameraBound(data.width, data.height);
        elkLog('收到共享源大小变化通知', data);
      });
      //生成图片
      onGenerateSnapshot((data) => {
        elkLog('收到生成图片通知', data);
        const snapshotId = data?.snapshotId;
        const res = drawPlugin.current?.exportAsBase64String({
          content: 'clip',
          type: 'png',
        });
        snapshot({
          snapshotId,
          base64Image: res ? res.content.replace(/data:image\/png;base64,/i, '') : '',
        });
      });
      onSelectTool((data) => {
        elkLog('收到selectTool通知', data);
        console.log('---onSelectTool', data);
        const { toolType, params } = data;
        tools.current?.useOftools(toolType, params);
      });
    }, []);

    const handleWebOpenNote = () => {
      note.setIsWebOpenNote(true);
    };
    // 监听画布操作
    return (
      <>
        <div className={s.note} ref={domRef}>
          <div ref={containerRef} style={screenDomSize} />
          {state === 'done' &&
            (isWeb || (isPad && !Platform.isPOPO)) &&
            note?.shareAnnotatePermission === NotePermission.ALL && (
              <NoteTools drawPlugin={drawPlugin.current}></NoteTools>
            )}
          {/* <div className={s.left}>
            <Button type="primary" onClick={() => drawPlugin.current?.clear()}>
              清除
            </Button>
            <div style={{ color: 'red' }}>
              远端大小: 宽{remoteSizeData.width},高:{remoteSizeData.height}
            </div>
          </div> */}
        </div>

        {!note?.shareAnnotateEnabled &&
          (isWeb || (isPad && !Platform.isPOPO)) &&
          note?.shareAnnotatePermission === NotePermission.ALL && (
            <OpenNoteBtn onClick={handleWebOpenNote} visible={true}></OpenNoteBtn>
          )}
      </>
    );
  })
);

export default Note;
